/**
 * useAuthConfig Hook
 *
 * Provides authentication middleware configuration for API hooks.
 * This hook abstracts the auth configuration to make it easy to switch
 * between local auth (development) and better-auth (staging/production).
 *
 * The hook reads tokens from the auth context and provides the configuration
 * needed by @cfleet/api-client authenticated hooks.
 *
 * @example
 * ```tsx
 * import { useAuthConfig } from '@/hooks/useAuthConfig';
 * import { useCustomerOrders } from '@cfleet/api-client';
 *
 * function OrderList() {
 *   const authConfig = useAuthConfig();
 *   const { data, isLoading } = useCustomerOrders(
 *     { page: 1, per_page: 20 },
 *     authConfig ? { authConfig } : undefined
 *   );
 *   // ...
 * }
 * ```
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@cfleet/auth';
import type { AuthMiddlewareConfig } from '@cfleet/api-client';

/**
 * Get API base URL from environment variables
 * Falls back to Envoy gateway URL for local development
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8443/api/v1';

/**
 * Hook to get authentication middleware configuration.
 *
 * This provides the configuration needed for authenticated API calls.
 * It handles:
 * - Token retrieval from auth context
 * - Auth failure callback (logout + redirect)
 * - Token refresh callback synchronization
 *
 * The configuration is memoized to prevent unnecessary re-renders.
 *
 * @returns AuthMiddlewareConfig for use with API hooks
 *
 * @example
 * ```tsx
 * const authConfig = useAuthConfig();
 * const { data } = useCustomerOrders({ page: 1 }, { authConfig });
 * ```
 */
export function useAuthConfig(): AuthMiddlewareConfig {
  const { logout, refreshToken } = useAuth();
  const navigate = useNavigate();

  return useMemo(
    (): AuthMiddlewareConfig => ({
      baseUrl: API_BASE_URL,

      /**
       * Called when authentication fails (401 after refresh attempt).
       * Logs out the user and redirects to login page.
       */
      onAuthFailure: () => {
        logout();
        navigate('/login', { replace: true });
      },

      /**
       * Called when tokens are successfully refreshed.
       * The auth middleware in @cfleet/api-client handles token storage,
       * but this callback can be used for additional synchronization if needed.
       */
      onTokenRefresh: async () => {
        // The api-client middleware handles token storage via setTokens
        // We can optionally trigger a refresh through the auth context here
        // if we need to sync state, but the middleware already handles this
        try {
          await refreshToken?.();
        } catch {
          // Refresh failed, auth middleware will call onAuthFailure
        }
      },
    }),
    [logout, navigate, refreshToken]
  );
}

/**
 * Hook variant that returns null if not authenticated.
 * Useful for conditional API calls where queries should be disabled
 * until the user is authenticated.
 *
 * @returns AuthMiddlewareConfig if authenticated, null otherwise
 *
 * @example
 * ```tsx
 * const authConfig = useAuthConfigIfAuthenticated();
 * // authConfig will be null if user is not logged in
 * const { data } = useCustomerOrders({ page: 1 }, authConfig ? { authConfig } : undefined);
 * ```
 */
export function useAuthConfigIfAuthenticated(): AuthMiddlewareConfig | null {
  const { isAuthenticated, tokens, logout, refreshToken } = useAuth();
  const navigate = useNavigate();

  return useMemo((): AuthMiddlewareConfig | null => {
    if (!isAuthenticated || !tokens) {
      return null;
    }

    return {
      baseUrl: API_BASE_URL,
      onAuthFailure: () => {
        logout();
        navigate('/login', { replace: true });
      },
      onTokenRefresh: async () => {
        try {
          await refreshToken?.();
        } catch {
          // Handled by onAuthFailure
        }
      },
    };
  }, [isAuthenticated, tokens, logout, navigate, refreshToken]);
}

/**
 * Hook to get auth config or throw if not authenticated.
 *
 * Use this in components that should only render when authenticated.
 * Throws an error if called without authentication.
 *
 * @returns AuthMiddlewareConfig (always defined)
 * @throws Error if not authenticated
 */
export function useRequiredAuthConfig(): AuthMiddlewareConfig {
  const authConfig = useAuthConfigIfAuthenticated();

  if (!authConfig) {
    throw new Error('Authentication required. This component should be wrapped in ProtectedRoute.');
  }

  return authConfig;
}

export default useAuthConfig;
